import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateWhatsAppGroupInput, UpdateWhatsAppGroupInput } from '@lms/api-client';
import { apiClient } from '../lib/api-client';

export const whatsappKeys = {
  all: ['whatsapp-groups'] as const,
  lists: () => [...whatsappKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...whatsappKeys.lists(), params] as const,
  details: () => [...whatsappKeys.all, 'detail'] as const,
  detail: (id: string) => [...whatsappKeys.details(), id] as const,
};

export function useWhatsAppGroups(params?: {
  gender?: string;
  programId?: string;
  skip?: number;
  take?: number;
}) {
  return useQuery({
    queryKey: whatsappKeys.list(params || {}),
    queryFn: () => apiClient.whatsappGroups.list(params),
  });
}

export function useWhatsAppGroup(id: string) {
  return useQuery({
    queryKey: whatsappKeys.detail(id),
    queryFn: () => apiClient.whatsappGroups.get(id),
    enabled: !!id,
  });
}

export function useCreateWhatsAppGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWhatsAppGroupInput) => apiClient.whatsappGroups.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: whatsappKeys.lists() });
    },
  });
}

export function useUpdateWhatsAppGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWhatsAppGroupInput }) =>
      apiClient.whatsappGroups.update(id, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: whatsappKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: whatsappKeys.lists() });
    },
  });
}

export function useDeleteWhatsAppGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.whatsappGroups.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: whatsappKeys.lists() });
    },
  });
}

export function useAddGroupMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: { phone: string; name: string } }) =>
      apiClient.whatsappGroups.addMember(groupId, data),
    onSuccess: (_, { groupId }) => {
      void queryClient.invalidateQueries({ queryKey: whatsappKeys.detail(groupId) });
    },
  });
}

export function useRemoveGroupMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, phone }: { groupId: string; phone: string }) =>
      apiClient.whatsappGroups.removeMember(groupId, phone),
    onSuccess: (_, { groupId }) => {
      void queryClient.invalidateQueries({ queryKey: whatsappKeys.detail(groupId) });
    },
  });
}
